import { useMemo } from "react"

import { DateRangePicker, Select } from "~/ui-rtk/components/ui/controls"
import { AggregationType } from "~/ui-rtk/constants/charts"
import useDate from "~/ui-rtk/hooks/date"

import { CUSTOM_PRESET_ID, type PeriodPreset } from "../utils/period-presets"
import type { CohortGranularity } from "../utils/cohort-matrix"

const dayjs = useDate()

export type PeriodSelectorProps = {
  granularity: CohortGranularity
  presets: PeriodPreset[]
  presetId: string
  range: { start: string; end: string }
  onPresetChange: (presetId: string) => void
  onCustomRange: (range: { start: string; end: string }) => void
}

/**
 * Preset dropdown (complete months/weeks, calendar years, halves, quarters —
 * built in utils/period-presets) plus a custom range picker. Custom weekly
 * ranges use the DAILY calendar deliberately: DateRangePicker's week mode
 * snaps to dayjs's Sunday weeks, not the company reporting week — the parent
 * snaps the picked dates to reporting weeks instead.
 */
export default function PeriodSelector({
  granularity,
  presets,
  presetId,
  range,
  onPresetChange,
  onCustomRange,
}: PeriodSelectorProps) {
  const options = useMemo(
    () => [
      ...presets.map(preset => ({ value: preset.id, label: preset.label })),
      { value: CUSTOM_PRESET_ID, label: "Custom" },
    ],
    [presets],
  )

  return (
    <div className="flex items-center gap-2">
      <Select
        value={presetId}
        options={options}
        onChange={event => onPresetChange(event.target.value)}
        className="w-44"
      />
      {presetId === CUSTOM_PRESET_ID && (
        <DateRangePicker
          start={dayjs(range.start, "YYYY-MM-DD").toDate()}
          end={dayjs(range.end, "YYYY-MM-DD").toDate()}
          comparisonEnabled={false}
          showPredefinedPeriods={false}
          granularity={
            granularity === "month"
              ? AggregationType.MONTHLY
              : AggregationType.DAILY
          }
          isMonthModeEnabled={granularity === "month"}
          onChange={({ start, end }) =>
            onCustomRange({
              start: dayjs(start).format("YYYY-MM-DD"),
              end: dayjs(end).format("YYYY-MM-DD"),
            })
          }
        />
      )}
    </div>
  )
}
